import { useState } from 'react'
import { useStore } from '../../store'

function Field({
  label,
  value,
  onChange,
  type = 'text',
  autoFocus,
}: {
  label: string
  value: string
  onChange: (v: string) => void
  type?: string
  autoFocus?: boolean
}) {
  return (
    <label className="block">
      <span className="mb-1 block text-[11px] uppercase tracking-[0.1em] text-fg-faint">{label}</span>
      <input
        autoFocus={autoFocus}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-lg border border-[var(--line)] bg-[var(--color-inset)] px-3 py-2 text-[13px] text-fg transition-colors focus:border-[var(--line-strong)] focus:outline-none"
      />
    </label>
  )
}

export function Auth() {
  const setupRequired = useStore((s) => s.setupRequired)
  const login = useStore((s) => s.login)
  const setupAdmin = useStore((s) => s.setupAdmin)
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [name, setName] = useState('')
  const [err, setErr] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    setErr(null)
    setBusy(true)
    try {
      if (setupRequired) await setupAdmin(username.trim(), password, name.trim() || undefined)
      else await login(username.trim(), password)
    } catch (e2) {
      setErr(e2 instanceof Error ? e2.message : 'Something went wrong')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="flex h-full items-center justify-center bg-[var(--color-void)] px-4">
      <div className="w-full max-w-sm">
        <div className="mb-6 flex items-center gap-2.5">
          <span className="orb-mini" style={{ width: 26, height: 26 }} aria-hidden />
          <span className="text-[15px] font-semibold tracking-[0.16em] text-fg">LIBERABANK</span>
        </div>
        <div className="card p-5">
          <h1 className="text-[18px] font-semibold text-fg">
            {setupRequired ? 'Create your admin account' : 'Sign in'}
          </h1>
          <p className="mt-1 text-[12.5px] text-fg-subtle">
            {setupRequired
              ? 'First-run setup — this is the owner account. You’ll add other users from the admin panel.'
              : 'Welcome back to your research desk.'}
          </p>
          <form onSubmit={submit} className="mt-4 space-y-2.5">
            {setupRequired && <Field label="Name (optional)" value={name} onChange={setName} />}
            <Field label="Username" value={username} onChange={setUsername} autoFocus />
            <Field label="Password" type="password" value={password} onChange={setPassword} />
            {err && <div className="text-[12px] text-down">{err}</div>}
            <button
              type="submit"
              disabled={busy || !username.trim() || password.length < (setupRequired ? 8 : 1)}
              className="mt-1 w-full rounded-lg bg-[var(--color-accent)] py-2.5 text-[13px] font-semibold text-[var(--color-base)] transition-opacity disabled:opacity-40"
            >
              {busy ? '…' : setupRequired ? 'Create admin & enter' : 'Sign in'}
            </button>
          </form>
        </div>
        <p className="mt-3 text-center text-[11px] text-fg-faint">
          {setupRequired
            ? 'Keep these credentials safe — they own the workspace.'
            : 'No account? Ask the admin to create one for you.'}
        </p>
      </div>
    </div>
  )
}
